/**
 * Casillas agrupadas — agrupar, filtrar, plegar y marcar en masa una lista de
 * sorteos que ya existe, sin cambiar lo que esa lista guarda.
 *
 * Dos mitades, a propósito separadas:
 *  - el MODELO (funciones puras sobre objetos planos): es lo que se prueba y lo
 *    que fija las reglas — filtrar y plegar conservan las marcas; la selección
 *    masiva no toca casillas ocultas ni deshabilitadas;
 *  - el PEGAMENTO con el navegador (pocas líneas): <details>/<summary> nativos,
 *    `checkbox.indeterminate` nativo, y eventos delegados. El HTML de cada
 *    casilla lo sigue produciendo quien lo producía, byte a byte: guardar lee
 *    las mismas clases, ids y data-attrs de siempre.
 *
 * El eje principal de agrupación es la rueda. Con los datos reales de Brasil
 * (5 ruedas × 3 mercados) agrupar por los tres ejes daba 15 grupos de un
 * sorteo — inútil —; por rueda da 5 de 3, y moneda e idioma van como etiqueta
 * secundaria en cada fila. Un juego sin rueda (Digi 12) cae entero en «Sin
 * rueda»; si solo sale un grupo, no se envuelve nada y la pantalla no cambia.
 *
 * El markup del acordeón copia el que ya usa el admin
 * (components/lotowheel-modals.js, «Visualizacion de los ceros»).
 */
import { FuzzySearch } from '../fuzzy-search.js';
import { escapeHtmlParaInyeccionSegura } from '../utils/escape-html-para-inyeccion-segura.js';
import { t } from '../services/i18n-admin.js';

const CLAVE_SIN_RUEDA = 'sin-rueda';
export const ETIQUETA_SIN_RUEDA = t('Sin rueda');

const SEPARADOR_DE_EJES_SECUNDARIOS = ' · ';

// ── Modelo ─────────────────────────────────────────────────────────────────

/**
 * Agrupa por rueda. Cada fila conserva el sorteo intacto y gana
 * `etiquetaSecundaria` («PYG · es») con los ejes que no agrupan.
 * Grupos ordenados por clave; «Sin rueda» siempre al final; dentro de cada
 * grupo, el orden de entrada (nada se reordena).
 *
 * @param {Array<{ id, nombre, ejes?: { tipoRuleta?, moneda?, idioma? } }>} sorteos
 * @returns {Array<{ clave: string, etiqueta: string, sorteos: Array }>}
 */
export function agruparSorteosPorEjes(
    sorteos,
    {
        etiquetaDeGrupo = clave => clave,
        claveDeGrupo = sorteo => sorteo.ejes?.tipoRuleta,
        // Cómo se llama el cubo de los que no tienen clave. Por defecto son
        // sorteos sin rueda; los pozos sin grupo no entienden de ruedas.
        etiquetaDelCuboSinClave = ETIQUETA_SIN_RUEDA,
    } = {}
) {
    const porClave = new Map();
    for (const sorteo of sorteos || []) {
        const ejes = sorteo.ejes || {};
        // Por defecto agrupa por rueda (sorteos); un módulo puede dar su propia
        // clave —los pozos agrupan por `grupo`— sin duplicar el componente.
        const clave = claveDeGrupo(sorteo) || CLAVE_SIN_RUEDA;
        if (!porClave.has(clave)) {
            porClave.set(clave, {
                clave,
                // La etiqueta humana de una rueda la conoce el módulo que la
                // pinta (Lotowheel tiene sus nombres); el componente solo
                // agrupa. Sin función, la clave; «Sin rueda» siempre traducida.
                etiqueta: clave === CLAVE_SIN_RUEDA ? etiquetaDelCuboSinClave : etiquetaDeGrupo(clave),
                sorteos: [],
            });
        }
        porClave.get(clave).sorteos.push({
            ...sorteo,
            etiquetaSecundaria: [ejes.moneda, ejes.idioma].filter(Boolean).join(SEPARADOR_DE_EJES_SECUNDARIOS),
        });
    }
    return [...porClave.values()].sort((a, b) => {
        if (a.clave === CLAVE_SIN_RUEDA) return 1;
        if (b.clave === CLAVE_SIN_RUEDA) return -1;
        return a.clave.localeCompare(b.clave);
    });
}

/** Con un solo grupo (o ninguno) no hay nada que envolver. */
export function hayQueAgrupar(grupos) {
    return Array.isArray(grupos) && grupos.length > 1;
}

/**
 * Estado del grupo para la casilla maestra y el contador. Cuenta TODAS las
 * hijas, visibles u ocultas: filtrar o plegar no cambia cuántas hay marcadas.
 */
export function estadoDeSeleccion(casillas) {
    const total = (casillas || []).length;
    let marcadas = 0;
    for (const casilla of casillas || []) if (casilla.marcada) marcadas++;
    return {
        marcadas,
        total,
        todas: total > 0 && marcadas === total,
        ninguna: marcadas === 0,
        indeterminado: marcadas > 0 && marcadas < total,
    };
}

/**
 * Marca o desmarca en masa SOLO las casillas visibles y habilitadas. Una
 * casilla oculta por el filtro o deshabilitada por permisos conserva su marca.
 * No muta la entrada.
 */
export function aplicarSeleccionMasiva(casillas, marcar) {
    return (casillas || []).map(casilla =>
        casilla.deshabilitada || casilla.oculta ? { ...casilla } : { ...casilla, marcada: Boolean(marcar) }
    );
}

/** Minúsculas y sin acentos, para que «LÍMA» encuentre «Lima». */
function normalizarParaBuscar(texto) {
    return String(texto || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();
}

/**
 * Índices de `textos` que coinciden con la consulta, con el buscador difuso
 * del admin (tolera una errata por término). Sin tope de resultados: el filtro
 * oculta, no pagina, y callar coincidencias sería mentir.
 */
function indicesQueCoinciden(textos, consulta) {
    const indexables = textos.map((texto, indice) => ({ indice, texto: normalizarParaBuscar(texto) }));
    const buscador = new FuzzySearch(indexables, ['texto'], { maxResults: Math.max(indexables.length, 1) });
    return new Set(buscador.search(consulta).map(resultado => resultado.item.indice));
}

/**
 * Filtra por texto cambiando SOLO la visibilidad (`oculta` en cada sorteo,
 * `oculto` en el grupo). Ninguna marca se toca. Texto vacío = todo visible.
 * Tolerante a mayúsculas y acentos; la coincidencia difusa es la del buscador
 * del admin (FuzzySearch), la misma que en el resto de pantallas.
 */
export function filtrarPorTexto(grupos, texto) {
    const consulta = normalizarParaBuscar(texto).trim();
    return (grupos || []).map(grupo => {
        if (consulta === '') {
            return { ...grupo, oculto: false, sorteos: grupo.sorteos.map(s => ({ ...s, oculta: false })) };
        }
        const coincidencias = indicesQueCoinciden(
            grupo.sorteos.map(s => s.nombre),
            consulta
        );
        const sorteos = grupo.sorteos.map((s, indice) => ({ ...s, oculta: !coincidencias.has(indice) }));
        return { ...grupo, sorteos, oculto: sorteos.every(s => s.oculta) };
    });
}

// ── HTML ───────────────────────────────────────────────────────────────────

/**
 * Envuelve el HTML de las casillas de un grupo en <details>/<summary> con
 * casilla maestra y contador. `contenidoHtml` viaja tal cual: lo produce quien
 * lo producía. Abierto por defecto si hay alguna marcada.
 *
 * `resumenHtml` (opcional) viaja DENTRO del <summary>, así que sigue visible con
 * el grupo plegado: es lo que hace que colapsar no cueste toda la información.
 * Lo produce el módulo —solo él sabe qué resume su dominio— y por eso llega ya
 * escapado; el componente no lo interpreta.
 */
export function htmlDeGrupoDeCasillas({
    idGrupo,
    etiqueta,
    estado,
    contenidoHtml,
    conMaestra = true,
    resumenHtml = '',
}) {
    // Con casillas: abierto solo si hay alguna marcada. Sin casillas (cards de
    // un listado) no hay nada que elegir, solo mirar: abierto entero.
    const abierto = !conMaestra || estado.marcadas > 0 ? ' open' : '';
    const maestra = conMaestra
        ? `<input type="checkbox" data-casillas-maestra="${escapeHtmlParaInyeccionSegura(idGrupo)}"${estado.todas ? ' checked' : ''}
                     title="${t('Marcar o desmarcar las visibles de este grupo')}">`
        : '';
    const contador = conMaestra ? `${estado.marcadas} ${t('de')} ${estado.total}` : `${estado.total}`;
    return `
        <details data-casillas-grupo="${escapeHtmlParaInyeccionSegura(idGrupo)}"${abierto}
                 style="margin:6px 0; background:var(--bg-tertiary); border-radius:8px; border:1px solid var(--border-glass, rgba(255,255,255,0.08)); overflow:hidden;">
          <summary style="padding:8px 12px; cursor:pointer; list-style:none; display:flex; align-items:center; justify-content:space-between; user-select:none; gap:10px;">
            <span style="display:flex; align-items:center; gap:8px;">
              ${maestra}
              <span style="font-size:13px; font-weight:600; color:var(--text-secondary);">${escapeHtmlParaInyeccionSegura(etiqueta)}</span>
            </span>
            <span style="display:flex; align-items:center; gap:8px; margin-left:auto;">
              ${resumenHtml}
              <span data-casillas-contador style="font-size:12px; color:var(--text-muted);">${contador}</span>
            </span>
          </summary>
          <div style="padding:6px 12px 10px;">${contenidoHtml}</div>
        </details>`;
}

/**
 * Lo que usan los módulos: decide plano o agrupado y ensambla filtro + grupos.
 * `htmlDeCasilla(sorteo)` lo sigue escribiendo el módulo (sus clases, ids y
 * data-attrs no cambian; recibe además `etiquetaSecundaria`). Con un solo grupo
 * devuelve las casillas planas: la pantalla no cambia. `envolverContenido`
 * permite, por ejemplo, poner las cards de cada grupo en su propia cuadrícula.
 */
export function htmlDeCasillasAgrupadas({
    idBase,
    sorteos,
    htmlDeCasilla,
    estaMarcada = () => false,
    conMaestra = true,
    envolverContenido = html => html,
    etiquetaDeGrupo,
    claveDeGrupo,
    etiquetaDelCuboSinClave,
    // Resumen del grupo visible incluso plegado. Recibe los sorteos del grupo;
    // por defecto no hay ninguno y la cabecera queda como estaba.
    resumenDeGrupo,
}) {
    const grupos = agruparSorteosPorEjes(sorteos, {
        ...(etiquetaDeGrupo ? { etiquetaDeGrupo } : {}),
        ...(claveDeGrupo ? { claveDeGrupo } : {}),
        ...(etiquetaDelCuboSinClave ? { etiquetaDelCuboSinClave } : {}),
    });
    if (!hayQueAgrupar(grupos)) {
        return grupos
            .flatMap(grupo => grupo.sorteos)
            .map(htmlDeCasilla)
            .join('');
    }
    return (
        htmlDeFiltroDeCasillas(idBase) +
        grupos
            .map(grupo =>
                htmlDeGrupoDeCasillas({
                    idGrupo: `${idBase}-${grupo.clave}`,
                    etiqueta: grupo.etiqueta,
                    estado: estadoDeSeleccion(grupo.sorteos.map(s => ({ marcada: Boolean(estaMarcada(s)) }))),
                    contenidoHtml: envolverContenido(grupo.sorteos.map(htmlDeCasilla).join('')),
                    conMaestra,
                    resumenHtml: resumenDeGrupo ? resumenDeGrupo(grupo.sorteos, grupo) : '',
                })
            )
            .join('')
    );
}

/**
 * Campo de filtro que va encima de los grupos, con el interruptor de plegado al
 * lado. Solo HTML; el pegamento lo escucha.
 *
 * El interruptor es UNO y alterna según lo que haya: si queda algún grupo
 * cerrado abre todos, y solo cuando están todos abiertos cierra. Dos botones
 * («expandir» y «colapsar») obligarían a mirar cuál está activo; uno que dice
 * lo que va a hacer se lee de un vistazo.
 */
export function htmlDeFiltroDeCasillas(idContenedor) {
    const id = escapeHtmlParaInyeccionSegura(idContenedor);
    return `
        <div style="display:flex; gap:8px; align-items:center; margin-bottom:8px;">
          <input type="search" class="form-input" data-casillas-filtro="${id}"
                 placeholder="${t('Buscar sorteo…')}" autocomplete="off"
                 style="flex:1; font-size:13px;">
          <button type="button" class="btn btn-secondary" data-casillas-plegado="${id}"
                  data-abrir="1" title="${t('Expandir o colapsar todos los grupos')}"
                  style="font-size:12px; white-space:nowrap; padding:6px 12px;">${t('Expandir todo')}</button>
        </div>`;
}

/**
 * Sincroniza el interruptor con el estado real de los grupos VISIBLES: si hay
 * alguno cerrado, ofrece abrir; si están todos abiertos, ofrece cerrar. Se
 * llama tras cada cambio (clic propio, filtro, o el usuario plegando a mano)
 * para que el botón nunca mienta sobre lo que va a hacer.
 */
function refrescarInterruptorDePlegado(contenedor) {
    const boton = contenedor.querySelector('[data-casillas-plegado]');
    if (!boton) return;
    const grupos = Array.from(contenedor.querySelectorAll('details[data-casillas-grupo]')).filter(d => !d.hidden);
    const hayCerrado = grupos.some(d => !d.open);
    boton.dataset.abrir = hayCerrado ? '1' : '0';
    boton.textContent = hayCerrado ? t('Expandir todo') : t('Colapsar todo');
}

// ── Pegamento con el navegador ─────────────────────────────────────────────

const ATRIBUTO_DE_FILA = 'data-casillas-fila';

/**
 * Activa maestra, contador y filtro dentro de `contenedor`. Idempotente: se
 * marca el contenedor para no duplicar oyentes en re-renders.
 *
 * Cada casilla hija debe estar dentro de un elemento con `data-casillas-fila`
 * (la <label> que ya existe) para que el filtro pueda ocultar la fila entera.
 * La maestra vive dentro del <summary>: su clic se intercepta para que no
 * pliegue el grupo, y se aplica a las hijas visibles y habilitadas — el mismo
 * criterio que `aplicarSeleccionMasiva`.
 */
const hijasDe = detalles => Array.from(detalles.querySelectorAll(`[${ATRIBUTO_DE_FILA}] input[type="checkbox"]`));

/**
 * Pone maestra y contador de un grupo según sus hijas. Un grupo sin maestra
 * (cards de un listado) no tiene casillas que contar: su contador es el total
 * fijado en el HTML y no se toca.
 */
function refrescarGrupo(detalles) {
    const maestra = detalles.querySelector('[data-casillas-maestra]');
    if (!maestra) return;
    const estado = estadoDeSeleccion(hijasDe(detalles).map(cb => ({ marcada: cb.checked })));
    maestra.checked = estado.todas;
    maestra.indeterminate = estado.indeterminado;
    const contador = detalles.querySelector('[data-casillas-contador]');
    if (contador) contador.textContent = `${estado.marcadas} ${t('de')} ${estado.total}`;
}

export function activarCasillasAgrupadas(contenedor) {
    if (!contenedor) return;
    // El refresco corre en CADA render: `indeterminate` no se puede expresar en
    // HTML y un contenedor persistente que se repinta (el modal de nodo) lo
    // perdería. Los oyentes, en cambio, se instalan una sola vez.
    for (const detalles of contenedor.querySelectorAll('details[data-casillas-grupo]')) refrescarGrupo(detalles);
    refrescarInterruptorDePlegado(contenedor);
    if (contenedor.dataset.casillasActivas === '1') return;
    contenedor.dataset.casillasActivas = '1';

    contenedor.addEventListener('click', evento => {
        const plegado = evento.target.closest('[data-casillas-plegado]');
        if (plegado) {
            const abrir = plegado.dataset.abrir === '1';
            // Solo los visibles: un grupo que el filtro dejó fuera no se toca,
            // así al limpiar el filtro vuelve como estaba.
            for (const detalles of contenedor.querySelectorAll('details[data-casillas-grupo]')) {
                if (!detalles.hidden) detalles.open = abrir;
            }
            refrescarInterruptorDePlegado(contenedor);
            return;
        }
        const maestra = evento.target.closest('[data-casillas-maestra]');
        if (!maestra) return;
        // Dentro de <summary> el clic también plegaría el grupo: se evita y se
        // aplica la marca a mano.
        evento.preventDefault();
        const detalles = maestra.closest('details');
        const hijas = hijasDe(detalles);
        const estado = estadoDeSeleccion(hijas.map(cb => ({ marcada: cb.checked })));
        const marcar = !estado.todas;
        for (const cb of hijas) {
            const fila = cb.closest(`[${ATRIBUTO_DE_FILA}]`);
            if (cb.disabled || (fila && fila.hidden)) continue;
            if (cb.checked !== marcar) {
                cb.checked = marcar;
                cb.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
        // Al cancelar el clic de un checkbox, el navegador REVIERTE `checked` e
        // `indeterminate` al terminar el despacho — DESPUÉS de los microtasks,
        // que se vacían al salir del oyente. Medido en Chromium con clic real:
        // con queueMicrotask la maestra quedaba congelada; con una tarea
        // (setTimeout 0) el refresco corre tras la reversión y se conserva.
        setTimeout(() => refrescarGrupo(detalles), 0);
    });

    contenedor.addEventListener('change', evento => {
        const cb = evento.target;
        if (!cb.matches(`[${ATRIBUTO_DE_FILA}] input[type="checkbox"]`)) return;
        const detalles = cb.closest('details[data-casillas-grupo]');
        if (detalles) refrescarGrupo(detalles);
    });

    contenedor.addEventListener('input', evento => {
        const filtro = evento.target.closest('[data-casillas-filtro]');
        if (!filtro) return;
        const consulta = normalizarParaBuscar(filtro.value).trim();
        for (const detalles of contenedor.querySelectorAll('details[data-casillas-grupo]')) {
            const filas = Array.from(detalles.querySelectorAll(`[${ATRIBUTO_DE_FILA}]`));
            let visibles = 0;
            if (consulta === '') {
                for (const fila of filas) fila.hidden = false;
                visibles = filas.length;
            } else {
                const coincidencias = indicesQueCoinciden(
                    filas.map(fila => fila.textContent),
                    consulta
                );
                filas.forEach((fila, indice) => {
                    fila.hidden = !coincidencias.has(indice);
                    if (!fila.hidden) visibles++;
                });
                if (visibles > 0) detalles.open = true;
            }
            detalles.hidden = visibles === 0;
        }
        refrescarInterruptorDePlegado(contenedor);
    });

    // El usuario también pliega a mano clicando el <summary>: `toggle` no burbujea,
    // así que se escucha en fase de captura para no perder esos cambios.
    contenedor.addEventListener(
        'toggle',
        evento => {
            if (evento.target.matches('details[data-casillas-grupo]')) refrescarInterruptorDePlegado(contenedor);
        },
        true
    );

    for (const detalles of contenedor.querySelectorAll('details[data-casillas-grupo]')) refrescarGrupo(detalles);
    refrescarInterruptorDePlegado(contenedor);
}
